import React, { useRef, useState, useEffect } from 'react';
import { useScrollAnimation, AnimationVariantName, AnimationVariant, ScrollAnimationOptions, animationVariants } from './useScrollAnimation';
import useDeviceDetect from '@/utils/hooks/useDeviceDetect';
// import './index.scss';

export interface AnimatedSectionProps {
    children: React.ReactNode;
    variant?: AnimationVariantName | AnimationVariant;
    options?: ScrollAnimationOptions;
    className?: string;
    style?: React.CSSProperties;
    as?: keyof JSX.IntrinsicElements;
    onClick?: () => void;
}

/**
 * 单个元素动画组件
 */
export const AnimatedSection: React.FC<AnimatedSectionProps> = ({
    children,
    variant = 'fadeInUp',
    options = {},
    className = '',
    style = {},
    as: Component = 'div',
    onClick,
}) => {
    // const { isH5 } = useDeviceDetect();
    const { ref, animationStyle } = useScrollAnimation(variant, options);

    return React.createElement(
        Component,
        {
            ref,
            className: `animated-section ${className}`,
            style: { ...animationStyle, ...style },
            onClick,
        },
        children,
    );
};

export interface AnimatedGridProps {
    children: React.ReactNode[];
    variant?: AnimationVariantName | AnimationVariant;
    options?: ScrollAnimationOptions;
    className?: string;
    style?: React.CSSProperties;
    itemClassName?: string;
    itemStyle?: React.CSSProperties;
    as?: keyof JSX.IntrinsicElements;
    itemAs?: keyof JSX.IntrinsicElements;
    onClick?: () => void;
}

/**
 * 多个元素交错动画组件
 */
export const AnimatedGrid: React.FC<AnimatedGridProps> = ({
    children,
    variant = 'fadeInUp',
    options = {},
    className = '',
    style = {},
    itemClassName = '',
    itemStyle = {},
    as: Component = 'div',
    itemAs: ItemComponent = 'div',
    onClick,
}) => {
    // const { isH5 } = useDeviceDetect();
    const containerRef = useRef<HTMLElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const childrenArray = React.Children.toArray(children);

    const animationConfig =
        typeof variant === 'string'
            ? variant in animationVariants
                ? animationVariants[variant as AnimationVariantName]
                : animationVariants.fadeInUp
            : variant;

    useEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        // 如果是移动端，直接设置为可见状态，不执行动画
        // if (isH5) {
        //     setIsVisible(true);
        //     return;
        // }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // 添加小延迟确保DOM已完全渲染
                    setTimeout(() => {
                        setIsVisible(true);
                    }, 50);
                    if (options.triggerOnce !== false) {
                        observer.unobserve(element);
                    }
                } else if (options.triggerOnce === false) {
                    setIsVisible(false);
                }
            },
            {
                threshold: options.threshold || 0.2,
                rootMargin: options.rootMargin || '0px 0px -50px 0px',
            },
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [options.threshold, options.rootMargin, options.triggerOnce]);

    return React.createElement(
        Component,
        {
            ref: containerRef,
            className: `animated-grid ${className}`,
            style,
            onClick,
        },
        childrenArray.map((child, index) => {
            // 将stagger转换为毫秒（如果传入的是秒）
            const staggerMs = (options.stagger || 0.15) * (options.stagger && options.stagger < 10 ? 1000 : 1);
            const delay = (options.delay || 0) + index * staggerMs;
            const shouldAnimate = isVisible;

            const animationStyle: React.CSSProperties = {
                ...animationConfig.initial,
                transition: animationConfig.transition || 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: shouldAnimate ? `${delay}ms` : '0ms',
                ...(shouldAnimate ? animationConfig.animate : {}),
            };

            // 如果子元素已经是一个完整的React元素，我们需要克隆它并添加动画属性
            if (React.isValidElement(child)) {
                return React.cloneElement(child as React.ReactElement<{ className?: string; style?: React.CSSProperties }>, {
                    key: index,
                    className: `${child.props.className || ''} animated-grid-item ${itemClassName}`.trim(),
                    style: {
                        ...child.props.style, // 先应用子组件的样式
                        ...itemStyle, // 再应用item样式
                        ...animationStyle, // 最后应用动画样式，确保不被覆盖
                    },
                });
            }

            // 如果不是React元素，则用指定的组件包装
            return React.createElement(
                ItemComponent,
                {
                    key: index,
                    className: `animated-grid-item ${itemClassName}`,
                    style: {
                        ...animationStyle,
                        ...itemStyle,
                    },
                },
                child,
            );
        }),
    );
};

export interface ProductShowcaseProps {
    title?: string;
    items: React.ReactNode[];
    className?: string;
    titleClassName?: string;
    itemsClassName?: string;
    itemClassName?: string;
    variant?: AnimationVariantName | AnimationVariant;
    titleVariant?: AnimationVariantName | AnimationVariant;
    options?: ScrollAnimationOptions;
}

/**
 * 产品展示组件 - 类似HTML示例中的product-showcase
 */
export const ProductShowcase: React.FC<ProductShowcaseProps> = ({
    title,
    items,
    className = '',
    titleClassName = '',
    itemsClassName = '',
    itemClassName = '',
    variant = 'rotateIn',
    titleVariant = 'fadeInUp',
    options = {},
}) => {
    return (
        <section className={`product-showcase ${className}`}>
            <div className="container">
                {title && (
                    <AnimatedSection variant={titleVariant} options={options} className={`product-showcase-title ${titleClassName}`} as="h2">
                        {title}
                    </AnimatedSection>
                )}

                <div className="product-container">
                    <AnimatedGrid
                        variant={variant}
                        options={{ stagger: 0.2, ...options }}
                        className={`product-images ${itemsClassName}`}
                        itemClassName={`product-image ${itemClassName}`}
                    >
                        {items}
                    </AnimatedGrid>
                </div>
            </div>
        </section>
    );
};

export default AnimatedSection;
